/**
 * ReviewQueueCard — one queue row as a phone card (BON-381).
 *
 * Two six-column tables don't survive a 390px viewport, so each row becomes a
 * card under the same two headings. Same deviations as the table: free-text
 * reason, age in days, and no Resolve (that needs a note, so it lives on the
 * detail page).
 *
 * The card IS the link. It used to be a `role="button"` div with `tabIndex={0}`
 * wrapping a real `<button>`, which ARIA forbids — a widget role must not contain
 * another interactive element, and a screen reader reaching the inner button
 * cannot say what it belongs to. One `<Link>` fixes that, and gets three things
 * the div never had for free: a real accessible role, open-in-new-tab, and a
 * visible target for a long-press.
 *
 * The two halves live in `components/` so this file is the card's shape and its
 * link behaviour, nothing else.
 */
import { Link } from 'react-router-dom';

import ReviewQueueCardFoot from './components/ReviewQueueCardFoot';
import ReviewQueueCardHead from './components/ReviewQueueCardHead';
import type { ReviewQueueItem } from './reviewTypes';

export interface ReviewQueueCardProps {
  item: ReviewQueueItem;
  muted?: boolean;
}

const CARD_STYLE = {
  display: 'block',
  padding: '12px 14px',
  borderRadius: 13,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  textAlign: 'left',
  color: 'inherit',
  textDecoration: 'none',
} as const;

export function ReviewQueueCard({ item, muted = false }: ReviewQueueCardProps) {
  return (
    <Link
      to={`/admin/needs-review/${item.kind}/${item.id}`}
      data-resolved={muted ? 'true' : 'false'}
      aria-label={`Open ${item.kind} ${item.reference}`}
      // De-emphasis by colour, never `opacity`: opacity multiplies through to
      // every descendant, so a dimmed card also dims whatever inside it has to
      // stay readable. The border and the text recede; nothing is made unreadable.
      style={{
        ...CARD_STYLE,
        ...(muted ? { background: 'var(--nb-surface)', color: 'var(--nb-muted)' } : {}),
      }}
    >
      <ReviewQueueCardHead item={item} />
      <ReviewQueueCardFoot item={item} />
    </Link>
  );
}
